import { Button, Heading, HStack, Link, VStack, Text as ChakraText, Center, Spinner } from '@chakra-ui/react'
import React, { useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useParams } from 'react-router-dom'
import type { Interceptors } from 'use-http'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import ImageReportGrid from '../../components/Performance/ImageReportGrid'
import NoteBox from '../../components/Performance/NoteBox'
import StatsGrid from '../../components/Performance/StatsGrid'
import type { PerformanceReportResponse } from '../../interfaces/Performance'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import FreePlanCTA from '../../components/FreePlan'
import CustomPlanCTA from '../../components/CustomPlan'
import { FeaturesEnum } from '../../../utils/features'

const PerformanceReportLayout = (): React.ReactElement | null => {
  const { deviceType, reportId } = useParams<{
    reportId?: string
    deviceType: 'desktop' | 'mobile'
  }>()

  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const interceptors: Interceptors<PerformanceReportResponse> = {
    response: async ({ response }) => {
      if (response.data?.mobile) {
        response.data.mobile.createdAt = response.data.createdAt
      }
      if (response.data?.desktop) {
        response.data.desktop.createdAt = response.data.createdAt
      }
      return response
    },
  }

  const { get: getReportFetch, data: reportResponse, loading } =
    useFetch<PerformanceReportResponse>({
      interceptors,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: true,
    })

  useEffect(() => {
    if (
      reportId &&
      user?.pricingPlan.featuresList.includes(FeaturesEnum.PERFORMANCE_CENTER)
    ) {
      const getReport = async (): Promise<void> => {
        await getReportFetch(
          `/api/performance/${user.clientNumber}/report/${reportId}`,
        )
      }
      void getReport()
    }
  }, [user])

  const report = useMemo(() => {
    if (reportResponse) {
      return reportResponse[deviceType]
    }
    return null
  }, [deviceType, reportResponse])

  if (loading) {
    return (
      <Center height="100%">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="appPaleGrey"
          color="appBlue"
          size="xl"
        />
      </Center>
    )
  }

  if (!report) {
    return <ChakraText>{t("report_not_found", "Report not found")}</ChakraText>
  }

  return (
    <React.Fragment>
      <MainContentHeadingBox>
        <Heading
          as="h5"
          size="md"
          fontWeight="semibold"
          color={colors.appDarkIndigo}
        >
          {t('detailed_performance_report', 'Deailed Image Performance Report')}
        </Heading>
        <HStack spacing={8}>
          <Link
            _hover={{ textDecoration: 'none', outline: 'none' }}
            href={`https://imagekit.io/website-analyzer/?url=${report.pageUrl}`}
            isExternal
          >
            <Button>
              {t('generate_latest_analysis', 'Generate Latest Analysis')}
            </Button>
          </Link>
          <Link
            as={NavLink}
            to={`/performance-alerts/summary/${deviceType}/${reportResponse?.urlId}`}
            _hover={{ textDecoration: 'none', outline: 'none' }}
          >
            <Button variant="outline">
              {t('view_historical_analysis', 'View Historical Analysis')}
            </Button>
          </Link>
        </HStack>
      </MainContentHeadingBox>

      <MainContentWrapper>
        {user?.pricingPlan.featuresList.includes(
          FeaturesEnum.PERFORMANCE_CENTER,
        ) ? (
          <VStack width="full" spacing={12} alignItems="start">
            <StatsGrid deviceType={deviceType} report={report} />

            <NoteBox url={report.pageUrl} />

            <Heading
              as="h6"
              fontSize="lg"
              color={colors.appDarkIndigo}
              fontWeight="bold"
            >
              {t('image_report', 'Image Report')}
            </Heading>

            {report.analyzedImageUrlList && (
              <ImageReportGrid
                analyzedImageUrlList={report.analyzedImageUrlList}
              />
            )}
          </VStack>
        ) : user?.pricingPlan.isPaidPlan ? (
          <CustomPlanCTA feature={FeaturesEnum.PERFORMANCE_CENTER} />
        ) : (
          <FreePlanCTA />
        )}
      </MainContentWrapper>
    </React.Fragment>
  )
}

export default PerformanceReportLayout
